import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FeedEmailData } from '../../mail/components/EmailFeedContent';
import { Task } from '../Interfaces';
import { Invoice } from '../../../types/accounting';
import { auth } from '../../../../firebase';
import { relTime } from '../../../themes/terminal/relTime';

/**
 * TerminalWorkspaceRail — left navigation column for the terminal-spirit
 * JobWorkspaceModal. One unified, keyboard-friendly list (commit-1 ships
 * mouse-only; keyboard nav is the next iteration).
 *
 * Sections:
 *   ▾ threads   — emails grouped by correspondent address (per-customer
 *                 thread view, not flat email list)
 *   ▾ tasks     — flat task list with status text prefix
 *   ▾ invoices  — flat invoice list with status + amount due
 *
 * (The legacy in-modal LLM chat section was retired 2026-06 — job chat now
 *  lives in the app-wide Andrei panel, opened via the [andrei] status-bar
 *  button. See project_agent_consolidation.)
 *
 * Each section is collapsible and renders a count next to the title.
 */

// ── Selection model ──────────────────────────────────────────────────────
// What the user has chosen to view in the stream pane.
// 'compose' is a new kind added for commit 2: an active email composition
// gets its own pane slot, follows the same FIFO eviction rules, and carries
// extra metadata about what it's replying to (if anything).
export type RailSelectionKind = 'thread' | 'task' | 'invoice' | 'compose';
export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward';

export interface ComposeMetadata {
  mode: ComposeMode;
  /** The thread this compose belongs to (drives send-success flash + drafts list). */
  threadAddress?: string;
  /** The email being replied to / forwarded (for reply/replyAll/forward). */
  replyToEmailId?: string;
  /** If loading an existing draft (resume), the Firestore draft doc id. */
  draftId?: string;
}

export interface RailSelection {
  kind: RailSelectionKind;
  /** For 'task'/'invoice' = doc id. For 'thread' = correspondent address.
   *  For 'compose' = a synthetic unique id (e.g. `compose-<timestamp>`). */
  id: string;
  compose?: ComposeMetadata;
}

export interface EmailThread {
  /** The correspondent address — what we group by. */
  address: string;
  /** Display name (best-effort: name field, falls back to address). */
  displayName: string;
  emails: FeedEmailData[];
  /** Most recent email's createdAt; drives sort + display. */
  lastAt: Date;
  /** Direction of most recent message: 'in' (← them) or 'out' (→ us). */
  lastDirection: 'in' | 'out';
  /** Inbound emails since the last outbound — surfaces "things needing reply". */
  unread: number;
}

// ── User's own email addresses (for outbound detection) ─────────────────
// Graph sync occasionally mis-tags sent emails (missing folder='sent' /
// emailType='sent'), or the user is self-CC'd on a thread, which caused
// a ghost thread to appear under the user's own name. Consulting the
// user's own address list and short-circuiting "if from is me, it's
// outbound" fixes this without hiding any emails.
function getUserAddresses(explicit?: string[]): string[] {
  const out = new Set<string>();
  if (explicit && explicit.length) {
    for (const a of explicit) {
      const v = String(a || '').toLowerCase().trim();
      if (v) out.add(v);
    }
  }
  const fbEmail = auth.currentUser?.email;
  if (fbEmail) out.add(String(fbEmail).toLowerCase().trim());
  const ls = localStorage.getItem('email');
  if (ls) out.add(String(ls).toLowerCase().trim());
  return Array.from(out);
}

// ── Group emails into per-correspondent threads ──────────────────────────
// We group by best-available address: inbound = from[0].address; outbound =
// to[0]. Same address across in+out forms one thread (the conversation).
//
// Outbound detection is now augmented: if the sender's address matches
// one of the current user's own addresses, the email is treated as
// outbound regardless of the folder/emailType fields. This handles:
//   - Graph sync where sent mail wasn't tagged folder='sent'
//   - Self-CC'd messages
//   - Legacy email docs missing the emailType field
//
// When picking the correspondent for an outbound email, we prefer a
// recipient that isn't the user themselves (so "ben cc'd ben + sarah"
// groups under sarah, not ben). Falls back to the first recipient if
// all recipients are the user.
export function groupEmailsByThread(
  emails: FeedEmailData[],
  userAddresses?: string[],
): EmailThread[] {
  const self = getUserAddresses(userAddresses);
  const isSelf = (addr: string): boolean => self.includes(String(addr || '').toLowerCase().trim());

  const computeIsOut = (e: FeedEmailData): boolean => {
    if (e.folder === 'sent' || e.emailType === 'sent') return true;
    const from = String(e.from?.[0]?.address || '').toLowerCase().trim();
    if (from && isSelf(from)) return true;
    return false;
  };

  const map = new Map<string, EmailThread>();
  for (const e of emails) {
    const isOut = computeIsOut(e);
    let addr = '';
    let name = '';
    if (isOut) {
      const toRaw = Array.isArray(e.to)
        ? e.to
        : (e.to ? [e.to] : []);
      const toList: string[] = toRaw
        .map(t => String(t || '').trim())
        .filter(Boolean);
      // Prefer a recipient that isn't the user; fall back to first if
      // all are the user (so self-only emails still appear somewhere).
      const primary = toList.find(t => !isSelf(t)) || toList[0] || '';
      addr = primary.toLowerCase();
      name = primary;
    } else {
      addr = String(e.from?.[0]?.address || '').toLowerCase().trim();
      name = e.from?.[0]?.name || addr;
    }
    if (!addr) continue;
    const existing = map.get(addr);
    if (existing) {
      existing.emails.push(e);
      // Keep the prettiest name we've seen for this address
      if (name && (!existing.displayName || existing.displayName === addr)) {
        existing.displayName = name;
      }
    } else {
      map.set(addr, {
        address: addr,
        displayName: name || addr,
        emails: [e],
        lastAt: e.createdAt,
        lastDirection: isOut ? 'out' : 'in',
        unread: 0,
      });
    }
  }
  // Finalize: sort each thread's emails newest-first, compute lastAt +
  // direction + unread. Direction + unread use the SAME "is-self-from"
  // logic as grouping so the rail's ←/→ arrow and [N] count stay
  // consistent with the thread assignment.
  for (const t of Array.from(map.values())) {
    t.emails.sort((a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0));
    const latest = t.emails[0];
    t.lastAt = latest.createdAt;
    t.lastDirection = computeIsOut(latest) ? 'out' : 'in';
    let count = 0;
    for (const e of t.emails) {
      if (computeIsOut(e)) break;
      count++;
    }
    t.unread = count;
  }
  // Sort threads by most recent activity
  return Array.from(map.values()).sort((a, b) => b.lastAt.getTime() - a.lastAt.getTime());
}

// relTime is imported from the shared terminal formatter. This file
// used to carry its own copy with slight formatting drift from the
// other terminal UI files — consolidating guarantees every timestamp
// across cards / rail / stream / status bar renders identically.

// ── Compact currency formatter ───────────────────────────────────────────
// Invoices should read "$4,200" not "$4200.00" in a dense rail row. Use the
// browser's number formatter for locale-correct thousands separators; strip
// trailing .00 for whole amounts.
function fmtMoney(amount: number, currency: string = 'AUD'): string {
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency', currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Bad currency code shouldn't break rendering
    return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
  }
}

// ── Section header (collapsible) ─────────────────────────────────────────
interface SectionProps {
  label: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}
const Section: React.FC<SectionProps> = ({ label, count, open, onToggle, children }) => (
  <div className="term-rail__section">
    <button type="button" className="term-rail__section-header" onClick={onToggle}>
      <span className="term-rail__section-arrow">{open ? '▾' : '▸'}</span>
      <span className="term-rail__section-label">{label}</span>
      {typeof count === 'number' && (
        <span className="term-rail__section-count">({count})</span>
      )}
    </button>
    {open && <div className="term-rail__section-body">{children}</div>}
  </div>
);

// ── Individual row primitive ─────────────────────────────────────────────
interface RowProps {
  active: boolean;
  prefix?: string;
  primary: string;
  secondary?: string;
  trailing?: React.ReactNode;
  onClick: () => void;
}
const Row: React.FC<RowProps> = ({ active, prefix, primary, secondary, trailing, onClick }) => (
  <button
    type="button"
    className={'term-rail__row' + (active ? ' term-rail__row--active' : '')}
    onClick={onClick}
  >
    <span className="term-rail__row-glyph" aria-hidden="true">
      {active ? '>' : (prefix ?? ' ')}
    </span>
    <span className="term-rail__row-primary">{primary}</span>
    {secondary && <span className="term-rail__row-secondary">{secondary}</span>}
    {trailing && <span className="term-rail__row-trailing">{trailing}</span>}
  </button>
);

// ── Inline rename primitive ──────────────────────────────────────────────
// Renders in place of a Row when the user hits [edit]. Enter commits,
// Escape cancels, blur also commits (same as macOS Finder / Gmail labels).
// Auto-focuses and selects the existing text so the user can just type.
interface RailRenameInputProps {
  initial: string;
  onCommit: (next: string) => void;
  onCancel: () => void;
}
const RailRenameInput: React.FC<RailRenameInputProps> = ({ initial, onCommit, onCancel }) => {
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);
  // Track whether we've committed so blur doesn't fire a duplicate save
  // after Enter.
  const committedRef = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const commit = () => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(value);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      className="term-rail__rename-input"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') { e.preventDefault(); committedRef.current = true; onCancel(); }
      }}
      onBlur={commit}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

// ── Main component ───────────────────────────────────────────────────────
export interface TerminalWorkspaceRailProps {
  emails: FeedEmailData[];
  tasks: Task[];
  invoices: Invoice[];
  /** All currently-open panes' selections. Any item present here highlights as active. */
  selections: RailSelection[];
  onSelectThread: (address: string) => void;
  onSelectTask: (task: Task) => void;
  onSelectInvoice: (invoice: Invoice) => void;
  /** Inline rename — rail replaces the row with an <input>, parent is
   *  called with the new title when the user commits. Firestore write is
   *  the parent's responsibility (keeps the rail purely presentational). */
  onRenameTask?: (taskId: string, newTitle: string) => void;
  onNewTask?: () => void;
  onNewInvoice: () => void;
  /** Fresh compose to this job's customer (no thread context / no reply target). */
  onNewEmail: () => void;
  // Section open/close state (lifted so layout can persist it)
  expanded: { threads: boolean; tasks: boolean; invoices: boolean };
  onToggleSection: (key: 'threads'|'tasks'|'invoices') => void;

  // ── Mobile-mode controls ───────────────────────────────────────────
  /** When true, render in compact-trigger / overlay mode instead of the
   *  desktop side-rail layout. */
  mobileMode?: boolean;
  /** When mobileMode is true: false = compact trigger only; true = full
   *  rail rendered as an overlay over the stream. */
  mobileOpen?: boolean;
  /** Toggle handler for the mobile open state. */
  onToggleMobile?: () => void;
}

const TerminalWorkspaceRail: React.FC<TerminalWorkspaceRailProps> = ({
  emails, tasks, invoices,
  selections,
  onSelectThread, onSelectTask, onSelectInvoice,
  onRenameTask,
  onNewTask, onNewInvoice, onNewEmail,
  expanded, onToggleSection,
  mobileMode = false,
  mobileOpen = false,
  onToggleMobile,
}) => {
  const threads = useMemo(() => groupEmailsByThread(emails), [emails]);

  // Inline-rename state — at most one row can be in rename mode. The row
  // swaps its Row component for a RailRenameInput that commits via the
  // relevant onRename<Kind> prop.
  const [renaming, setRenaming] = useState<{ kind: 'task'; id: string } | null>(null);

  // An item is "active" if any pane is currently showing it.
  const isActive = (kind: RailSelectionKind, id: string) =>
    selections.some(s => s.kind === kind && s.id === id);

  // ── Mobile collapsed state: render only a compact trigger row ──────
  // Single line under the status bar: "[≡ menu] · 2 threads · 0 tasks
  // · 0 invoices". Tap toggles the full rail as an overlay.
  // Total height ~28px so 80%+ of the viewport stays free for content.
  if (mobileMode && !mobileOpen) {
    return (
      <button
        type="button"
        className="term-rail-trigger"
        onClick={onToggleMobile}
        aria-expanded={false}
        aria-label="Open navigation"
      >
        <span className="term-rail-trigger__glyph" aria-hidden="true">≡</span>
        <span className="term-rail-trigger__label">menu</span>
        <span className="term-rail-trigger__sep">·</span>
        <span className="term-rail-trigger__count">{threads.length} thread{threads.length !== 1 ? 's' : ''}</span>
        <span className="term-rail-trigger__sep">·</span>
        <span className="term-rail-trigger__count">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
        <span className="term-rail-trigger__sep">·</span>
        <span className="term-rail-trigger__count">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</span>
      </button>
    );
  }

  // ── Mobile-overlay header (rendered above the sections when open) ──
  // Mirrors the trigger row but with a [×] to close.
  const mobileHeader = mobileMode && mobileOpen ? (
    <div className="term-rail__mobile-header">
      <span className="term-rail-trigger__glyph" aria-hidden="true">≡</span>
      <span className="term-rail-trigger__label">menu</span>
      <span className="term-rail__mobile-spacer" />
      <button
        type="button"
        className="term-rail__mobile-close"
        onClick={onToggleMobile}
        aria-label="Close navigation"
      >
        [×]
      </button>
    </div>
  ) : null;

  return (
    <aside className={
      'term-rail'
      + (mobileMode && mobileOpen ? ' term-rail--mobile-overlay' : '')
    }>
      {mobileHeader}

      {/* ── threads ────────────────────────────────────────────────── */}
      <Section
        label="threads"
        count={threads.length}
        open={expanded.threads}
        onToggle={() => onToggleSection('threads')}
      >
        {threads.length === 0 ? (
          <div className="term-rail__empty">no emails yet</div>
        ) : threads.map(t => (
          <Row
            key={t.address}
            active={isActive('thread', t.address)}
            primary={t.displayName}
            secondary={`${relTime(t.lastAt)} ${t.lastDirection === 'in' ? '←' : '→'}`}
            trailing={t.unread > 0 ? (
              <span
                className={
                  'term-rail__unread'
                  + (t.lastDirection === 'in'
                      && (Date.now() - t.lastAt.getTime()) > 48*3600*1000
                    ? ' term-rail__unread--stale'
                    : ' term-rail__unread--waiting')
                }
              >
                [{t.unread}]
              </span>
            ) : undefined}
            onClick={() => onSelectThread(t.address)}
          />
        ))}
      </Section>

      {/* ── tasks ──────────────────────────────────────────────────── */}
      <Section
        label="tasks"
        count={tasks.length}
        open={expanded.tasks}
        onToggle={() => onToggleSection('tasks')}
      >
        {tasks.length === 0 ? (
          <div className="term-rail__empty">no tasks yet</div>
        ) : tasks.map(t => {
          const title = t.title || '(untitled)';
          const status = (t.status === 'done' ? 'completed' : t.status) || 'pending';
          const isRenaming = onRenameTask && renaming?.kind === 'task' && renaming.id === t.id;

          if (isRenaming) {
            return (
              <div key={t.id} className="term-rail__row-wrap term-rail__row-wrap--renaming">
                <RailRenameInput
                  initial={title}
                  onCommit={(next) => {
                    const trimmed = next.trim();
                    if (trimmed && trimmed !== title) onRenameTask(t.id, trimmed);
                    setRenaming(null);
                  }}
                  onCancel={() => setRenaming(null)}
                />
              </div>
            );
          }

          return (
            <div key={t.id} className="term-rail__row-wrap">
              <Row
                active={isActive('task', t.id)}
                primary={title}
                secondary={status}
                onClick={() => onSelectTask(t)}
              />
              {onRenameTask && (
                <button
                  type="button"
                  className="term-rail__row-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRenaming({ kind: 'task', id: t.id });
                  }}
                  title={`Rename "${title}"`}
                  aria-label={`Rename task ${title}`}
                >
                  [edit]
                </button>
              )}
            </div>
          );
        })}
      </Section>

      {/* ── invoices ───────────────────────────────────────────────── */}
      <Section
        label="invoices"
        count={invoices.length}
        open={expanded.invoices}
        onToggle={() => onToggleSection('invoices')}
      >
        {invoices.length === 0 ? (
          <div className="term-rail__empty">no invoices yet</div>
        ) : invoices.map(inv => {
          const total = Number(inv.total) || 0;
          const due   = Number(inv.amountDue) || 0;
          const currency = inv.currencyCode || 'AUD';
          const status = (inv.status || 'DRAFT').toLowerCase();
          // Show the invoice's value prominently; append "· Xdue" only when
          // there's still money outstanding. Matches the classic InvoiceListItem
          // logic so paid invoices don't spuriously render as $0.
          const parts = [status, fmtMoney(total, currency)];
          if (due > 0 && status !== 'paid' && status !== 'voided') {
            parts.push(`${fmtMoney(due, currency)} due`);
          }
          return (
            <Row
              key={inv.id}
              active={isActive('invoice', inv.id)}
              primary={inv.invoiceNumber || `#${inv.id.slice(0,5)}`}
              secondary={parts.join(' · ')}
              onClick={() => onSelectInvoice(inv)}
            />
          );
        })}
      </Section>

      {/* ── footer actions ─────────────────────────────────────────── */}
      <div className="term-rail__footer">
        <button type="button" className="term-rail__action" onClick={onNewEmail}   title="New email to this customer">
          <span className="term-rail__action-bracket">[</span>+email<span className="term-rail__action-bracket">]</span>
        </button>
        {onNewTask && <button type="button" className="term-rail__action" onClick={onNewTask}    title="New task">
          <span className="term-rail__action-bracket">[</span>+task<span className="term-rail__action-bracket">]</span>
        </button>}
        <button type="button" className="term-rail__action" onClick={onNewInvoice} title="New invoice">
          <span className="term-rail__action-bracket">[</span>+invoice<span className="term-rail__action-bracket">]</span>
        </button>
      </div>
    </aside>
  );
};

export default TerminalWorkspaceRail;
